/**
 * Practice Settings Page
 *
 * Page for managing organization settings including patient visibility
 * and templates configuration.
 * Part of the "Your Practice" feature.
 */

import { EPrescriptionOrgSection, useJotRxSetupState } from '@features/eprescriptions';
import { PatientRequirementsSection } from '@features/patient-requirements';
import { OrgTemplatesSection } from '@features/templates/components';
import { useCan } from '@features/user';
import { Box, Typography } from '@mui/material';
import { FeatureGates, useFeatureGate } from '@services/statsig';
import { hasRole, ROLES } from '@utils/roleUtils';

import { OrganizationInfoCards } from './components/OrganizationInfoCards';
import { OrganizationPreferencesTable } from './components/OrganizationPreferencesTable';
import { PermissionsMatrix } from './components/PermissionsMatrix';
import { ReportAccessMatrix } from './components/ReportAccessMatrix';
import { useOrganizationAccess } from './store';

export default function PracticeSettingsPage() {
  const { organizationData, userState } = useOrganizationAccess();
  const { isJotRxEnabled, isFullySetup } = useJotRxSetupState();
  // ADR 0073 Phase 3 (GDN-1590): catalog parity with hasAdminPrivileges
  // (is_admin_equivalent breadth) — see test_permissions.py BE parity tests.
  const isOrgAdmin = useCan('manage_organization');
  // Both matrices' backend routes are practice-admin guarded; gate the cards
  // to match so broader org-admin roles never see cards their fetches 403 on.
  const isPracticeAdmin = hasRole(
    userState?.group_user_roles,
    ROLES.PRACTICE_ADMINISTRATOR,
  );

  // Show e-prescription section only when enabled and setup is incomplete
  const showEPrescriptionSetup = isJotRxEnabled && !isFullySetup;

  // GDN-3224 (AC-1): the requirement catalog is practice-admin territory (PRD
  // §3 gives the catalog to the practice admin), and the whole feature stays
  // dark until the org's gate flips — deploy is not launch.
  const patientRequirementsEnabled = useFeatureGate(FeatureGates.PATIENT_REQUIREMENTS);
  const showPatientRequirements = patientRequirementsEnabled && isPracticeAdmin;

  return (
    <>
      {/* E-Prescription Settings — above org info when setup is incomplete */}
      {showEPrescriptionSetup && (
        <Box id="eprescription-org-section" sx={{ mb: 3 }}>
          <EPrescriptionOrgSection />
        </Box>
      )}

      {/* Organization Info */}
      <OrganizationInfoCards
        organization={organizationData}
        activeMembers={[]}
        inactiveMembers={[]}
        variant="info"
        canEditName={isPracticeAdmin}
      />

      {/* Organization preferences — patient visibility, note signing, … */}
      <Box sx={{ mb: 3 }}>
        <OrganizationPreferencesTable />
      </Box>

      {showPatientRequirements && (
        <Box sx={{ mb: 3 }}>
          <PatientRequirementsSection />
        </Box>
      )}

      {isPracticeAdmin && (
        <>
          <Box sx={{ mb: 3 }}>
            <PermissionsMatrix />
          </Box>
          <Box sx={{ mb: 3 }}>
            <ReportAccessMatrix />
          </Box>
        </>
      )}

      {/* Resources Section (only for org admins) */}
      {isOrgAdmin && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
            Resources
          </Typography>
          <OrgTemplatesSection />
        </Box>
      )}
    </>
  );
}
